/**
 * BroadcastResendModal - Modal para reenviar broadcast com seleção de audiência
 * Versão mobile-first
 */
import React, { useState, useEffect } from 'react'
import ReactMarkdown from 'react-markdown'
import { RefreshCw, Bell, Mail, Users, Eye, Send, ArrowLeft } from 'lucide-react'
import { Modal } from '../../components/molecules/Modal'
import Card from '../../components/atoms/Card'
import Button from '../../components/atoms/Button'
import Select from '../../components/atoms/Select'
import UserMultiSelect from '../../components/molecules/UserMultiSelect'
import { formatNumber } from '../../core/admin.utils'
import {
	AUDIENCE_OPTIONS,
	INITIAL_RESEND_FORM,
	MARKDOWN_PREVIEW_STYLES,
} from '../core/broadcast.constants'
import { getBroadcastContent, buildResendPayload } from '../core/broadcast.utils'

const BroadcastResendModal = ({
	isOpen,
	onClose,
	broadcast,
	onConfirm,
	loading,
	previewLoading,
	previewStats,
	onPreview,
}) => {
	const [step, setStep] = useState('select') // select | preview
	const [resendForm, setResendForm] = useState(INITIAL_RESEND_FORM)

	// Reset state when modal opens/closes
	useEffect(() => {
		if (isOpen && broadcast) {
			setStep('select')
			setResendForm({
				...INITIAL_RESEND_FORM,
				targetAudience: broadcast.targetAudience || 'all',
			})
		}
	}, [isOpen, broadcast])

	if (!broadcast) return null

	const content = getBroadcastContent(broadcast)

	const handleAudienceChange = (value) => {
		setResendForm(prev => ({
			...prev,
			targetAudience: value,
			customUserIds: value === 'custom' ? prev.customUserIds : [],
		}))
	}

	const handlePreviewClick = () => {
		const payload = buildResendPayload(broadcast, resendForm)
		if (onPreview) {
			onPreview(payload, () => setStep('preview'))
		} else {
			setStep('preview')
		}
	}

	const handleConfirm = () => {
		const payload = buildResendPayload(broadcast, resendForm)
		onConfirm(broadcast._id, payload)
	}

	const canPreview = resendForm.targetAudience !== 'custom' ||
		resendForm.customUserIds.length > 0

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title="Reenviar Broadcast"
			size="lg"
		>
			<div className="space-y-4 md:space-y-6">
				<style>{MARKDOWN_PREVIEW_STYLES}</style>

				{step === 'select' && (
					<>
						{/* Preview do conteúdo original */}
						<Card className="p-3 md:p-4">
							<h4 className="font-semibold text-text text-sm md:text-base mb-2 md:mb-3 flex items-center gap-2">
								<RefreshCw className="w-4 h-4" />
								Conteúdo Original
							</h4>

							<div className="space-y-3 md:space-y-4">
								{content.pushTitle && (
									<div className="bg-base-400 rounded-lg p-2 md:p-3">
										<div className="flex items-center gap-2 mb-1 md:mb-2">
											<Bell className="w-4 h-4 text-blue-500" />
											<span className="text-xs font-medium text-blue-500">Push</span>
										</div>
										<p className="font-medium text-text text-xs md:text-sm">{content.pushTitle}</p>
										<div className="markdown-preview text-text-muted text-xs mt-1 line-clamp-2">
											<ReactMarkdown>{content.pushBody}</ReactMarkdown>
										</div>
									</div>
								)}

								{content.emailSubject && (
									<div className="bg-base-400 rounded-lg p-2 md:p-3">
										<div className="flex items-center gap-2 mb-1 md:mb-2">
											<Mail className="w-4 h-4 text-green-500" />
											<span className="text-xs font-medium text-green-500">Email</span>
										</div>
										<p className="font-medium text-text text-xs md:text-sm">{content.emailSubject}</p>
										{content.emailTitle && (
											<p className="text-text text-xs mt-1">{content.emailTitle}</p>
										)}
										<div className="markdown-preview text-text-muted text-xs mt-1 line-clamp-2">
											<ReactMarkdown>{content.emailBody}</ReactMarkdown>
										</div>
									</div>
								)}
							</div>
						</Card>

						{/* Seleção de Audiência */}
						<Card className="p-3 md:p-4">
							<h4 className="font-semibold text-text text-sm md:text-base mb-2 md:mb-3 flex items-center gap-2">
								<Users className="w-4 h-4" />
								Destinatários
							</h4>

							<div className="space-y-3 md:space-y-4">
								<Select
									label="Audiência"
									value={resendForm.targetAudience}
									onChange={(e) => handleAudienceChange(e.target.value)}
									options={AUDIENCE_OPTIONS}
								/>

								{resendForm.targetAudience === 'custom' && (
									<UserMultiSelect
										label="Selecionar Usuários"
										value={resendForm.customUserIds}
										onChange={(users) => setResendForm(prev => ({
											...prev,
											customUserIds: users,
										}))}
										placeholder="Buscar usuários..."
									/>
								)}
							</div>
						</Card>

						{/* Actions - Mobile friendly */}
						<div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3 pt-2 border-t border-base-200 sm:border-0 sm:pt-0">
							<Button
								variant="outline"
								onClick={onClose}
								disabled={loading || previewLoading}
								className="w-full sm:w-auto"
							>
								Cancelar
							</Button>
							<Button
								onClick={handlePreviewClick}
								disabled={!canPreview}
								loading={previewLoading}
								className="w-full sm:w-auto"
							>
								<Eye className="w-4 h-4 mr-2" />
								Visualizar
							</Button>
						</div>
					</>
				)}

				{step === 'preview' && (
					<>
						{/* Preview Stats */}
						<Card className="p-3 md:p-4">
							<h4 className="font-semibold text-text text-sm md:text-base mb-2 md:mb-3">Prévia do Reenvio</h4>

							<div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4 mb-3 md:mb-4">
								<div className="text-center p-2 md:p-3 bg-base-400 rounded-lg">
									<p className="text-xl md:text-2xl font-bold text-text">
										{formatNumber(previewStats?.totalTargeted || resendForm.customUserIds.length || 0)}
									</p>
									<p className="text-xs text-text-muted">Total de Usuários</p>
								</div>
								{content.pushTitle && (
									<div className="text-center p-2 md:p-3 bg-base-400 rounded-lg">
										<p className="text-xl md:text-2xl font-bold text-blue-500">
											{formatNumber(previewStats?.byPlatform?.withPushToken || 0)}
										</p>
										<p className="text-xs text-text-muted">Receberão Push</p>
									</div>
								)}
								{content.emailSubject && (
									<div className="text-center p-2 md:p-3 bg-base-400 rounded-lg">
										<p className="text-xl md:text-2xl font-bold text-green-500">
											{formatNumber(previewStats?.byPlatform?.withEmail || 0)}
										</p>
										<p className="text-xs text-text-muted">Receberão Email</p>
									</div>
								)}
							</div>

							<p className="text-xs md:text-sm text-text-muted text-center">
								Audiência: <span className="font-medium">
									{AUDIENCE_OPTIONS.find(a => a.value === resendForm.targetAudience)?.label || 'Todos'}
								</span>
							</p>
						</Card>

						{/* Resumo do Conteúdo */}
						<Card className="p-3 md:p-4">
							<h4 className="font-semibold text-text text-sm md:text-base mb-2 md:mb-3">Conteúdo a Enviar</h4>
							<div className="space-y-2 md:space-y-3">
								{content.pushTitle && (
									<div className="flex items-start gap-2">
										<Bell className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
										<div className="min-w-0">
											<p className="text-xs md:text-sm font-medium text-text truncate">{content.pushTitle}</p>
										</div>
									</div>
								)}
								{content.emailSubject && (
									<div className="flex items-start gap-2">
										<Mail className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
										<div className="min-w-0">
											<p className="text-xs md:text-sm font-medium text-text truncate">{content.emailSubject}</p>
										</div>
									</div>
								)}
							</div>
						</Card>

						{/* Actions - Mobile friendly */}
						<div className="flex flex-col sm:flex-row justify-between gap-2 sm:gap-3 pt-2 border-t border-base-200 sm:border-0 sm:pt-0">
							<Button
								variant="outline"
								onClick={() => setStep('select')}
								disabled={loading}
								className="w-full sm:w-auto order-2 sm:order-1"
							>
								<ArrowLeft className="w-4 h-4 mr-2 sm:mr-1" />
								<span className="sm:hidden">Voltar</span>
								<span className="hidden sm:inline">Voltar</span>
							</Button>
							<div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 order-1 sm:order-2">
								<Button
									variant="outline"
									onClick={onClose}
									disabled={loading}
									className="w-full sm:w-auto"
								>
									Cancelar
								</Button>
								<Button
									variant="primary"
									onClick={handleConfirm}
									loading={loading}
									className="w-full sm:w-auto"
								>
									<Send className="w-4 h-4 mr-2" />
									<span className="hidden sm:inline">Confirmar Reenvio</span>
									<span className="sm:hidden">Reenviar</span>
								</Button>
							</div>
						</div>
					</>
				)}
			</div>
		</Modal>
	)
}

export default BroadcastResendModal
